import { useQuery, useMutation } from '@apollo/client/react'
import { GET_CLIENT } from '@/lib/operations/clients'
import {
  GET_PUBLISH_QUEUE,
  ADD_TO_PUBLISH_QUEUE,
  PUBLISH_ITEM_NOW,
  APPROVE_PUBLISH_ITEM,
  REJECT_PUBLISH_ITEM,
  EDIT_PUBLISH_ITEM,
  SCHEDULE_PUBLISH_ITEM,
  DELETE_PUBLISH_ITEM,
  CLEAR_PUBLISH_QUEUE,
} from '@/lib/operations/content'
import type { BuyerJourneyStage, GqlPublishQueueItem, PublishQueueItem } from '@/lib/types'

// ─── Read hooks ───────────────────────────────────────────────────────────────

export function usePublishQueue(clientId: string) {
  const { data, loading, error } = useQuery<{ client: { publishQueue?: PublishQueueItem[] } }>(GET_CLIENT, {
    variables: { id: clientId },
    skip: !clientId,
  })
  return {
    items: data?.client?.publishQueue ?? [],
    loading,
    error,
  }
}

export function usePublishQueueFull(clientId: string) {
  const { data, loading, error } = useQuery<{ publishQueue: GqlPublishQueueItem[] }>(GET_PUBLISH_QUEUE, {
    variables: { clientId },
    skip: !clientId,
  })
  return {
    items: data?.publishQueue ?? [],
    loading,
    error,
  }
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────

interface SeoMetadataInput {
  metaTitle?: string
  metaDescription?: string
  slug?: string
}

export function useAddToQueue() {
  const [mutate, { loading }] = useMutation<{ addToPublishQueue: PublishQueueItem }>(ADD_TO_PUBLISH_QUEUE)
  const addToQueue = (
    clientId: string,
    contentType: string,
    content: string,
    runId?: string | null,
    seoMetadata?: SeoMetadataInput,
    _buyerJourneyStage?: BuyerJourneyStage,
  ) =>
    mutate({
      variables: { clientId, runId, contentType, content, seoMetadata },
      refetchQueries: [{ query: GET_PUBLISH_QUEUE, variables: { clientId } }],
    })
  return { addToQueue, loading }
}

export function usePublishItem() {
  const [mutate, { loading }] = useMutation<{ publishItemNow: PublishQueueItem }>(PUBLISH_ITEM_NOW)
  const publishNow = (clientId: string, itemId: string) =>
    mutate({
      variables: { clientId, itemId },
      refetchQueries: [{ query: GET_PUBLISH_QUEUE, variables: { clientId } }],
    })
  return { publishNow, loading }
}

export function useApproveItem() {
  const [mutate, { loading }] = useMutation<{ approvePublishItem: PublishQueueItem }>(APPROVE_PUBLISH_ITEM)
  const approve = (clientId: string, itemId: string, toEmail?: string) =>
    mutate({
      variables: { clientId, itemId, toEmail },
      refetchQueries: [
        { query: GET_CLIENT, variables: { id: clientId } },
        { query: GET_PUBLISH_QUEUE, variables: { clientId } },
      ],
    })
  return { approve, loading }
}

export function useRejectItem() {
  const [mutate, { loading }] = useMutation<{ rejectPublishItem: PublishQueueItem }>(REJECT_PUBLISH_ITEM)
  const reject = (clientId: string, itemId: string, feedback?: string) =>
    mutate({
      variables: { clientId, itemId, feedback },
      refetchQueries: [
        { query: GET_CLIENT, variables: { id: clientId } },
        { query: GET_PUBLISH_QUEUE, variables: { clientId } },
      ],
    })
  return { reject, loading }
}

export function useEditItem() {
  const [mutate, { loading }] = useMutation<{ editPublishItem: PublishQueueItem }>(EDIT_PUBLISH_ITEM)
  const edit = (clientId: string, itemId: string, content?: string, toEmail?: string) =>
    mutate({
      variables: { clientId, itemId, content, toEmail },
      refetchQueries: [
        { query: GET_CLIENT, variables: { id: clientId } },
        { query: GET_PUBLISH_QUEUE, variables: { clientId } },
      ],
    })
  return { edit, loading }
}

export function useScheduleItem() {
  const [mutate, { loading }] = useMutation<{ schedulePublishItem: PublishQueueItem }>(SCHEDULE_PUBLISH_ITEM)
  const schedule = (clientId: string, itemId: string, scheduledFor: string) =>
    mutate({
      variables: { clientId, itemId, scheduledFor },
      refetchQueries: [
        { query: GET_CLIENT, variables: { id: clientId } },
        { query: GET_PUBLISH_QUEUE, variables: { clientId } },
      ],
    })
  return { schedule, loading }
}

export function useDeleteItem() {
  const [mutate, { loading }] = useMutation<{ deletePublishItem: boolean }>(DELETE_PUBLISH_ITEM)
  const deleteItem = (clientId: string, itemId: string) =>
    mutate({
      variables: { clientId, itemId },
      refetchQueries: [
        { query: GET_CLIENT, variables: { id: clientId } },
        { query: GET_PUBLISH_QUEUE, variables: { clientId } },
      ],
    })
  return { deleteItem, loading }
}

export function useClearQueue() {
  const [mutate, { loading }] = useMutation<{ clearPublishQueue: number }>(CLEAR_PUBLISH_QUEUE)
  const clearQueue = (clientId: string, statuses?: string[]) =>
    mutate({
      variables: { clientId, statuses },
      refetchQueries: [
        { query: GET_CLIENT, variables: { id: clientId } },
        { query: GET_PUBLISH_QUEUE, variables: { clientId } },
      ],
    })
  return { clearQueue, loading }
}
